import React from "react";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "../../../context/LanguageContext";

type Option = {
  label: string;
  value: string;
};

type SelectFieldProps = {
  label: string;
  icon: LucideIcon;
  required?: boolean;
  error?: string;
  options: Option[];
  /** Overrides the default "Select {label}" placeholder option — pass a
   *  translated string for fields that need one. */
  placeholder?: string;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

const SelectField = ({
  label,
  icon: Icon,
  required = false,
  error,
  options,
  placeholder,
  ...props
}: SelectFieldProps) => {
  const { t } = useLanguage();
  return (
  <div>
    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
      <Icon size={16} className="me-2 text-[#b3d130]" />
      {label}
      {required && <span className="text-red-500 ms-1">*</span>}
    </label>

    <select
      {...props}
      className={`w-full px-4 py-3 border rounded-xl focus:border-[#b3d130] focus:outline-none focus:ring-2 focus:ring-[#b3d130]/20 transition-all duration-300 ${error
          ? "border-red-500 bg-red-50"
          : "border-gray-300"
        }`}
    >
      <option value="">{placeholder ?? t("common.selectPlaceholder", { label })}</option>

      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>

    {error && (
      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
        <span>⚠️</span>
        {error}
      </p>
    )}
  </div>
  );
};

export default SelectField;